import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { Input } from '@monorepo/base/src/components/form/input/input';
import { Dropdown } from '@monorepo/base/src/components/dropdown/dropdown';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { IAdminxStore } from '../../../stores';
import { CampaignsApi } from '../../../apis/campaigns.api';
import styles from './domain-factors.form.module.scss';

function parseCampaignIdFromLabel(label: string): string {
	const idPrefix = label.match(/^(\d+)\s*-\s*/);
	return idPrefix?.[1] ?? '';
}

export const DomainFactorsForm = observer(() => {
	const { domainFactorsStore } = useStores<IAdminxStore>();
	const domainFactorCrudStore = domainFactorsStore.getCrud();
	const formStore = domainFactorCrudStore.getFormStore();
	const factor = domainFactorCrudStore.getData();
	const [campaignOptions, setCampaignOptions] = useState<string[]>([]);
	const [isCampaignsLoading, setIsCampaignsLoading] = useState(false);
	const factorValue = factor.getDailyMaxLossFactor();

	useEffect(() => {
		let cancelled = false;
		setIsCampaignsLoading(true);
		CampaignsApi.list()
			.then(campaigns => {
				if (!cancelled) {
					setCampaignOptions(campaigns?.map(c => `${c.id} - ${c.name}`) ?? []);
				}
			})
			.finally(() => {
				if (!cancelled) {
					setIsCampaignsLoading(false);
				}
			});
		return () => {
			cancelled = true;
		};
	}, []);

	return (
		<div className={styles.form}>
			<Dropdown
				label="Campaign"
				placeholder="Select campaign"
				autocomplete
				autocompletePlaceholder="Search by name or campaign ID"
				isLoading={isCampaignsLoading}
				disabled={isCampaignsLoading || campaignOptions.length === 0}
				options={campaignOptions}
				defaultOption={campaignOptions.find(o => o.startsWith(`${factor.getCampaignId() ?? ''} -`))}
				onSelect={val => {
					const label = val?.values().next().value;
					factor.setCampaignId(label ? parseCampaignIdFromLabel(label) : '');
				}}
				isCloseIcon={true}
				error={formStore.getErrors().get('campaign_id')?.getMsg()}
			/>
			<Input
				topLabel="Domain"
				value={factor.getDomain() ?? ''}
				onValue={value => {
					factor.setDomain(value);
					formStore.clearError('domain');
				}}
				required
				requiredAsterisk={false}
				error={formStore.getErrors().get('domain')?.getMsg()}
			/>
			<Input
				topLabel="Daily max loss factor"
				value={factorValue == null ? '' : String(factorValue)}
				onValue={next => {
					formStore.clearError('daily_max_loss_factor');
					if (next === '') {
						factor.setDailyMaxLossFactor(undefined);
						return;
					}
					const parsed = Number(next);
					if (!Number.isNaN(parsed)) {
						factor.setDailyMaxLossFactor(parsed);
					}
				}}
				required
				requiredAsterisk={false}
				error={formStore.getErrors().get('daily_max_loss_factor')?.getMsg()}
			/>
		</div>
	);
});
